import { usePrincipalKey } from "@auth/sessionStore";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import {
  fetchJevCallDetail,
  fetchJevCallPage,
  fetchJevTick,
  fetchJevTickPage,
  type JevCallFilters,
} from "./jevCallClient";
import { fetchOnce } from "./traceQueries";

export const useJevCallPage = (
  active: boolean,
  filters: JevCallFilters,
  cursor: string | undefined,
) => {
  const principal = usePrincipalKey();
  return useQuery({
    queryKey: ["jev-calls", principal, "list", filters, cursor] as const,
    queryFn: ({ signal }) => fetchJevCallPage(cursor, filters, signal),
    enabled: active && principal !== "anon",
    placeholderData: keepPreviousData,
    ...fetchOnce,
  });
};

/** Brian's picture links (`STILL_URL_SECONDS`) last a minute; a detail is reused
 * only while they still work. */
const LINKS_LAST_MS = 50_000;

// An open row keeps its pictures' links fresh.
const freshLinks = {
  staleTime: LINKS_LAST_MS,
  gcTime: LINKS_LAST_MS,
  refetchInterval: LINKS_LAST_MS,
  refetchOnWindowFocus: false,
} as const;

export const useJevCallDetail = (callUuid: string, active: boolean) => {
  const principal = usePrincipalKey();
  return useQuery({
    queryKey: ["jev-calls", principal, "detail", callUuid] as const,
    queryFn: ({ signal }) => fetchJevCallDetail(callUuid, signal),
    enabled: active && principal !== "anon",
    ...freshLinks,
  });
};

export const useJevTickPage = (
  active: boolean,
  roomUuid: string,
  cursor: string | undefined,
) => {
  const principal = usePrincipalKey();
  return useQuery({
    queryKey: ["jev-calls", principal, "ticks", roomUuid, cursor] as const,
    queryFn: ({ signal }) => fetchJevTickPage(cursor, roomUuid, signal),
    enabled: active && principal !== "anon",
    placeholderData: keepPreviousData,
    ...fetchOnce,
  });
};

/** A tick's crop links last as long as a call detail's. */
export const useJevTick = (
  roomUuid: string,
  tickedAt: string,
  active: boolean,
) => {
  const principal = usePrincipalKey();
  return useQuery({
    queryKey: ["jev-calls", principal, "tick", roomUuid, tickedAt] as const,
    queryFn: ({ signal }) => fetchJevTick(roomUuid, tickedAt, signal),
    enabled: active && principal !== "anon",
    ...freshLinks,
  });
};
